import { NotificationEvent } from '../../types/notifications';

/**
 * Where a notification takes you when you click it.
 *
 * Returns null when there is nowhere useful to go; callers render those rows
 * inert rather than inventing a destination, so the panel only grows an
 * affordance where it actually leads somewhere.
 *
 * Two rules keep this honest against the per-user daily sweep (2026-07-27):
 *
 *  - Route on `searchId`, and on `entityId` only where `entityType` names
 *    what it is. The sweep's `entityId` is a USER uuid, and reading it as a
 *    search id would deep-link into `/searches/<user-id>`, which dead-ends on
 *    "Search not found".
 *  - Distrust `searchId` on a user-scoped event. It is null there today; if a
 *    future payload carries a stray one, a per-user event must still land on
 *    the whole feed, because that is what it is about.
 */
export const notificationTarget = (
  event: Pick<NotificationEvent, 'notificationType' | 'entityType' | 'entityId' | 'searchId'>
): string | null => {
  const searchId = event.entityType === 'user' ? null : event.searchId;

  switch (event.notificationType) {
    // First run carries a search id, so the feed opens already filtered to the
    // search that just finished — the page reads `?searchId=` on mount. The
    // daily sweep has none and lands on everything new, which is the point.
    case 'opportunities_ready':
      return searchId ? `/opportunities?searchId=${searchId}` : '/opportunities';

    case 'search_status_changed':
    case 'crawl_progress':
    case 'search_idle_warning':
    case 'search_auto_paused':
      return searchId ? `/searches/${searchId}` : '/searches';

    case 'application_status_changed':
    case 'contact_enrichment_completed':
    case 'retry_contact_ready':
    case 'recommended_action_changed':
      return '/applications';

    // About one opportunity, which is the entity (D19). entityId is read only
    // when entityType says it is an opportunity; otherwise the feed.
    case 'contact_switched':
    case 'contact_switch_proposed':
    case 'contact_unreachable':
      return event.entityType === 'opportunity' && event.entityId
        ? `/opportunities/${event.entityId}`
        : '/opportunities';

    default:
      return null;
  }
};
